import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { clinicsApi, monthlyReviewsApi } from '../services/api';
import { invalidateLookup } from '../services/lookupCache';
import { useAuth } from './AuthContext';

const ClinicContext = createContext(null);

const STORAGE_KEY = 'currentClinicId';

export const useClinic = () => {
  const ctx = useContext(ClinicContext);
  if (!ctx) {
    throw new Error('useClinic must be used within a ClinicProvider');
  }
  return ctx;
};

export const ClinicProvider = ({ children }) => {
  const { isAuthenticated, token } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [currentClinicId, setCurrentClinicIdState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [monthlyReviewsTherapistEnabled, setMonthlyReviewsTherapistEnabled] = useState(true);
  const [monthlyReviewsVisibilityLoaded, setMonthlyReviewsVisibilityLoaded] = useState(false);
  const lastTokenRef = useRef(null);

  const fetchClinics = useCallback(async () => {
    if (!isAuthenticated) {
      setClinics([]);
      return [];
    }
    setLoading(true);
    setError(null);
    try {
      const response = await clinicsApi.getMine();
      const list = response.data?.data || [];
      setClinics(list);
      // If no current selected clinic, try restoring from membership defaults
      const stored = localStorage.getItem(STORAGE_KEY);
      const storedId = stored ? Number(stored) : null;
      const validStored = list.find(c => c.id === storedId);
      if (validStored) {
        if (storedId !== currentClinicId) setCurrentClinicIdState(storedId);
      } else if (list.length > 0) {
        const def = list.find(c => c.is_default) || list[0];
        localStorage.setItem(STORAGE_KEY, String(def.id));
        setCurrentClinicIdState(def.id);
      } else {
        localStorage.removeItem(STORAGE_KEY);
        setCurrentClinicIdState(null);
      }
      return list;
    } catch (err) {
      console.error('Fetch clinics error:', err);
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, currentClinicId]);

  useEffect(() => {
    if (!isAuthenticated || !currentClinicId) {
      setMonthlyReviewsTherapistEnabled(true);
      setMonthlyReviewsVisibilityLoaded(!isAuthenticated);
      return undefined;
    }

    let cancelled = false;
    setMonthlyReviewsVisibilityLoaded(false);
    monthlyReviewsApi
      .getVisibility()
      .then((response) => {
        if (cancelled) return;
        setMonthlyReviewsTherapistEnabled(response.data?.data?.therapist_enabled !== false);
      })
      .catch(() => {
        if (cancelled) return;
        setMonthlyReviewsTherapistEnabled(true);
      })
      .finally(() => {
        if (!cancelled) setMonthlyReviewsVisibilityLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, currentClinicId]);

  // Fetch clinics on auth change
  useEffect(() => {
    if (isAuthenticated && token && token !== lastTokenRef.current) {
      lastTokenRef.current = token;
      fetchClinics();
    } else if (!isAuthenticated) {
      lastTokenRef.current = null;
      setClinics([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  // Sync state when something else (e.g. the axios response interceptor on a
  // CLINIC_REQUIRED / CLINIC_FORBIDDEN error) clears the stored clinic id.
  useEffect(() => {
    const onExternalChange = (e) => {
      const detail = e?.detail || {};
      const incoming = detail.clinicId == null ? null : Number(detail.clinicId);
      setCurrentClinicIdState((prev) => (prev === incoming ? prev : incoming));
    };
    window.addEventListener('clinic:change', onExternalChange);
    return () => window.removeEventListener('clinic:change', onExternalChange);
  }, []);

  const setCurrentClinicId = useCallback((newId) => {
    const idNum = newId ? Number(newId) : null;
    if (idNum === currentClinicId) return;

    if (idNum) {
      localStorage.setItem(STORAGE_KEY, String(idNum));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    setCurrentClinicIdState(idNum);

    invalidateLookup();

    try {
      window.dispatchEvent(new CustomEvent('clinic:change', { detail: { clinicId: idNum } }));
    } catch (e) {
      // no-op
    }

    // Force a full platform reload so every screen re-fetches data
    // under the newly selected clinic context.
    window.location.reload();
  }, [currentClinicId]);

  const currentClinic = clinics.find(c => c.id === currentClinicId) || null;

  const value = {
    clinics,
    currentClinic,
    currentClinicId,
    setCurrentClinicId,
    loading,
    error,
    refresh: fetchClinics,
    hasClinics: clinics.length > 0,
    needsClinicPicker: clinics.length > 0 && !currentClinicId,
    monthlyReviewsTherapistEnabled,
    setMonthlyReviewsTherapistEnabled,
    monthlyReviewsVisibilityLoaded,
  };

  return (
    <ClinicContext.Provider value={value}>
      {children}
    </ClinicContext.Provider>
  );
};
